import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Check, Sparkles, ShoppingBag, Wand2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from 'shared/components/ui/Card';
import Button from 'shared/components/ui/Button';
import { virtualTryOn, formatApiError, getPrepUploadUrl, uploadToS3 } from 'api/fashionAnalysisApi';
import { processImageForUpload, getExtensionFromMimeType } from 'utils/imageProcessing';
import { demoStyles } from './demoStyles';
import { URLS } from 'configs';
import './AITryOnDemo.scss';

// Timing constants
const SCROLL_DELAY = 100; // ms - Delay before auto-scroll
const OFFERS_LOAD_TIME = 1500; // ms - Online offers loading time
const SCROLL_OFFSET = -20; // px - Offset from top when scrolling

/**
 * AITryOnDemo Component
 * Reusable virtual try-on interface for both home page and dedicated page
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showTitle - Whether to show the section title (default: true)
 */
const AITryOnDemo = ({ className = '', showTitle = true }) => {
  const { t } = useTranslation();
  const [userPhoto, setUserPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [gender, setGender] = useState('women');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generatedImage, setGeneratedImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [error, setError] = useState(null);
  const [isRateLimitError, setIsRateLimitError] = useState(false);
  const [detectedOutfit, setDetectedOutfit] = useState(null);
  const resultRef = useRef(null);

  const handlePhotoUpload = e => {
    const file = e.target.files?.[0];
    if (file) {
      setUserPhoto(file);
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success('Photo uploaded! Now select a style to try on.');
    }
  };

  const handleGenerateTryOn = async () => {
    if (!userPhoto || !selectedStyle) {
      toast.error('Please upload your photo and select a style first.');
      return;
    }

    setIsGenerating(true);
    setIsLoadingOffers(true);
    setGeneratedImage('');
    setError(null);

    // Auto-scroll to loading section immediately
    setTimeout(() => {
      if (resultRef.current) {
        const element = resultRef.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + SCROLL_OFFSET;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, SCROLL_DELAY);

    try {
      // Step 1: Process and upload user's photo
      toast.info('Uploading your photo...');

      const { blob, mimeType } = await processImageForUpload(userPhoto);
      const fileExtension = getExtensionFromMimeType(mimeType);

      const uploadResponse = await getPrepUploadUrl(fileExtension);

      await uploadToS3(uploadResponse.uploadUrl, blob, mimeType);

      const userPhotoFileId = uploadResponse.fileId;

      if (!userPhotoFileId) {
        throw new Error('Failed to get file ID from upload');
      }

      // Step 2: Get outfit description from selected style
      const selectedStyleData = demoStyles[gender].find(s => s.id === selectedStyle);
      if (!selectedStyleData) {
        throw new Error('Style not found');
      }

      const { outfitJson } = selectedStyleData;

      if (!outfitJson) {
        throw new Error('Outfit JSON not found in selected style');
      }

      toast.info('AI generating your virtual try-on...', { duration: 8000 });

      // Step 3: Call virtual try-on API with user's photo as target
      const result = await virtualTryOn(userPhotoFileId, outfitJson);

      setGeneratedImage(result.generatedImage);
      setDetectedOutfit(outfitJson); // Store outfit data for display
      setIsGenerating(false);

      const processingTime = result.metadata?.processingTime
        ? `${(result.metadata.processingTime / 1000).toFixed(1)}s`
        : '';
      toast.success(
        `Virtual try-on complete! ${
          processingTime ? `Generated in ${processingTime}` : 'Your personalized result is ready!'
        }`,
      );

      // Load online offers
      setTimeout(() => {
        setIsLoadingOffers(false);
      }, OFFERS_LOAD_TIME);
    } catch (err) {
      console.error('Virtual try-on error:', err);
      setError(formatApiError(err));
      setIsGenerating(false);
      setIsLoadingOffers(false);
      setGeneratedImage('');
      setIsRateLimitError(err.status === 429 && err.limitType === 'per_day');

      if (err.status === 429 && err.limitType === 'per_day') {
        toast.error('Daily limit reached. Create a free account to get unlimited virtual try-ons!');
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  return (
    <div className={`ai-tryon ${className}`}>
      <div className='ai-tryon__container'>
        {showTitle && (
          <div className='ai-tryon__header'>
            <h2 className='ai-tryon__title'>
              {t('ai_tryon_title_1', 'See Styles')}{' '}
              <span className='ai-tryon__title--gradient'>{t('ai_tryon_title_2', 'On You')}</span>
            </h2>

            <p className='ai-tryon__description'>
              {t(
                'ai_tryon_description',
                'Upload your photo and select a style. Our AI will create a realistic image showing how the outfit looks on you, plus find all items online to buy.',
              )}
            </p>
          </div>
        )}

        {/* Demo Grid */}
        <div className='ai-tryon__demo-grid'>
          {/* Upload Section */}
          <Card className='ai-tryon__demo-card'>
            <h3>{t('tryon_upload_title', 'Upload Your Photo')}</h3>
            <div className='ai-tryon__upload-area'>
              {photoPreview ? (
                <img src={photoPreview} alt='Preview' />
              ) : (
                <div className='ai-tryon__upload-placeholder'>
                  <Upload />
                  <p>{t('tryon_upload_placeholder', 'Click or drag your photo here')}</p>
                </div>
              )}
              <input type='file' accept='image/*' onChange={handlePhotoUpload} className='ai-tryon__file-input' />
            </div>
          </Card>

          {/* Style Selection */}
          <Card className='ai-tryon__demo-card'>
            <div className='ai-tryon__style-header'>
              <h3>{t('tryon_select_style', 'Select a Style')}</h3>
              <div className='ai-tryon__gender-select'>
                <label>
                  <input
                    type='radio'
                    value='women'
                    checked={gender === 'women'}
                    onChange={e => setGender(e.target.value)}
                  />
                  {t('women', 'Women')}
                </label>
                <label>
                  <input
                    type='radio'
                    value='men'
                    checked={gender === 'men'}
                    onChange={e => setGender(e.target.value)}
                  />
                  {t('men', 'Men')}
                </label>
              </div>
            </div>
            <div className='ai-tryon__styles-grid'>
              {demoStyles[gender].map(style => (
                <div
                  key={style.id}
                  className={`ai-tryon__style-item ${selectedStyle === style.id ? 'selected' : ''}`}
                  onClick={() => setSelectedStyle(style.id)}
                  onKeyPress={e => e.key === 'Enter' && setSelectedStyle(style.id)}
                  role='button'
                  tabIndex={0}
                >
                  <img src={style.image} alt={style.name} />
                  <span className='ai-tryon__style-label'>{style.name}</span>
                  {selectedStyle === style.id && (
                    <div className='ai-tryon__style-check'>
                      <Check />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Button
              onClick={handleGenerateTryOn}
              disabled={!userPhoto || !selectedStyle || isGenerating}
              className='ai-tryon__generate-button'
            >
              {isGenerating ? (
                <>
                  <Loader2 className='spin' />
                  {t('tryon_generating', 'Generating...')}
                </>
              ) : (
                <>
                  <Wand2 />
                  {t('tryon_generate', 'Generate Try-On')}
                </>
              )}
            </Button>
          </Card>
        </div>

        {/* Results Section */}
        {(isGenerating || generatedImage || error) && (
          <div ref={resultRef} className='ai-tryon__results'>
            {/* Error State */}
            {error && (
              <Card className='ai-tryon__error-card'>
                <AlertCircle className='ai-tryon__error-icon' />
                <h3>{t('tryon_error_rate_limit_title', 'Daily Limit Reached')}</h3>
                <p>{t('tryon_error_rate_limit_subtitle', 'Try again tomorrow or create a free account to try now')}</p>
                {isRateLimitError && (
                  <Button href={URLS.CONTACT} className='ai-tryon__error-cta'>
                    {t('contact_us', 'Contact us')}
                  </Button>
                )}
              </Card>
            )}

            {/* Generated Result or Loading */}
            {!error && (isGenerating || generatedImage) && (
              <Card className='ai-tryon__result-card'>
                <h3>
                  <Check className='success-icon' />
                  {t('tryon_result', 'Your Virtual Try-On')}
                </h3>
                <div className='ai-tryon__result-image'>
                  {isGenerating ? (
                    <div className='ai-tryon__loading-overlay'>
                      <Sparkles className='ai-tryon__loading-icon pulse' />
                      <p>{t('tryon_generating_title', 'AI Generating Your Look...')}</p>
                      <div className='ai-tryon__progress-bar'>
                        <div className='ai-tryon__progress-fill' />
                      </div>
                    </div>
                  ) : (
                    <>
                      <img src={generatedImage} alt='Generated try-on result' />
                      <p className='ai-tryon__result-note'>
                        {t('tryon_result_note', '✨ AI-generated personalized try-on result')}
                      </p>
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* AI Detected Fashion Elements */}
            {!isGenerating && !error && detectedOutfit && (
              <Card className='ai-tryon__ai-elements-card'>
                <h3>
                  <Sparkles />
                  {t('tryon_ai_detected', 'AI Detected Fashion Elements')}
                </h3>
                <p className='ai-tryon__ai-description'>
                  {t('tryon_ai_description', 'Our AI analyzed the outfit and identified these fashion items:')}
                </p>
                <div className='ai-tryon__elements-grid'>
                  {/* Upper Garment */}
                  {detectedOutfit.upper_garment && (
                    <div className='ai-tryon__element-tag'>
                      <div className='ai-tryon__element-icon'>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                          <path d='M12 2L2 7l10 5 10-5-10-5z' />
                          <path d='M2 17l10 5 10-5M2 12l10 5 10-5' />
                        </svg>
                      </div>
                      <div className='ai-tryon__element-info'>
                        <span className='ai-tryon__element-name'>
                          {detectedOutfit.upper_garment.type.charAt(0).toUpperCase() +
                            detectedOutfit.upper_garment.type.slice(1)}
                        </span>
                        <span className='ai-tryon__element-confidence'>
                          {detectedOutfit.upper_garment.color} • {detectedOutfit.upper_garment.material}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Lower Garment */}
                  {detectedOutfit.lower_garment && (
                    <div className='ai-tryon__element-tag'>
                      <div className='ai-tryon__element-icon'>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                          <rect x='4' y='4' width='16' height='16' rx='2' />
                        </svg>
                      </div>
                      <div className='ai-tryon__element-info'>
                        <span className='ai-tryon__element-name'>
                          {detectedOutfit.lower_garment.type.charAt(0).toUpperCase() +
                            detectedOutfit.lower_garment.type.slice(1)}
                        </span>
                        <span className='ai-tryon__element-confidence'>
                          {detectedOutfit.lower_garment.color} • {detectedOutfit.lower_garment.material}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Footwear */}
                  {detectedOutfit.footwear && (
                    <div className='ai-tryon__element-tag'>
                      <div className='ai-tryon__element-icon'>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                          <path d='M8 21l8-11-8-4-5 11 5 4z' />
                        </svg>
                      </div>
                      <div className='ai-tryon__element-info'>
                        <span className='ai-tryon__element-name'>
                          {detectedOutfit.footwear.type.charAt(0).toUpperCase() + detectedOutfit.footwear.type.slice(1)}
                        </span>
                        <span className='ai-tryon__element-confidence'>
                          {detectedOutfit.footwear.color} • {detectedOutfit.footwear.material}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Accessories */}
                  {detectedOutfit.accessories &&
                    detectedOutfit.accessories.map((accessory, index) => (
                      <div key={`accessory-${index}`} className='ai-tryon__element-tag'>
                        <div className='ai-tryon__element-icon'>
                          <svg viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                            <rect x='4' y='8' width='16' height='12' rx='2' />
                            <path d='M8 8V6a4 4 0 0 1 8 0v2' />
                          </svg>
                        </div>
                        <div className='ai-tryon__element-info'>
                          <span className='ai-tryon__element-name'>
                            {accessory.type.charAt(0).toUpperCase() + accessory.type.slice(1)}
                          </span>
                          <span className='ai-tryon__element-confidence'>
                            {accessory.color} • {accessory.material}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            )}

            {/* Shopping Results with separate loading */}
            {!isGenerating && !error && (
              <Card className='ai-tryon__shopping-card'>
                <h3>
                  <ShoppingBag />
                  {t('tryon_shop_look', 'Shop This Look')}
                </h3>

                {!generatedImage ? (
                  <div className='ai-tryon__shop-cta'>
                    <p>
                      {t('tryon_shop_description', 'Upload a photo and try on a style to discover matching items!')}
                    </p>
                    <Button href={URLS.CONTACT} className='ai-tryon__shop-cta-button'>
                      <Upload />
                      {t('contact_us', 'Contact us')}
                    </Button>
                  </div>
                ) : isLoadingOffers ? (
                  <div className='ai-tryon__offers-loading'>
                    <div className='ai-tryon__loading-content'>
                      <ShoppingBag className='ai-tryon__loading-icon pulse' />
                      <p>{t('tryon_searching_products', 'Searching online stores for matching items...')}</p>
                      <div className='ai-tryon__progress-bar'>
                        <div className='ai-tryon__progress-fill' />
                      </div>
                    </div>
                    <div className='ai-tryon__products-skeleton'>
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className='ai-tryon__skeleton-card'>
                          <div className='ai-tryon__skeleton-image shimmer' />
                          <div className='ai-tryon__skeleton-info'>
                            <div className='ai-tryon__skeleton-text shimmer' />
                            <div className='ai-tryon__skeleton-text short shimmer' />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='ai-tryon__shop-message'>
                      <p>
                        {t(
                          'tryon_account_benefits',
                          'Create a free account to save this look, track prices, and get unlimited try-ons!',
                        )}
                      </p>
                      <div className='ai-tryon__shop-actions'>
                        <Button href={URLS.CONTACT} className='ai-tryon__cta-button'>
                          {t('contact_us', 'Contact us')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AITryOnDemo;
